import { dispatch } from 'lib/store'
import log from 'lib/log'
import apiClient from 'lib/api-client'
import {
  SET_LOG_EVENTS_COUNT_DATA,
  SET_FETCHING_LOG_EVENTS_COUNT_DATA,
} from 'constants/action-types'
import { getCommonQueryFilters, isDemoLoading } from 'selectors/search/filters'
import { getChartData } from 'selectors/search/chart'
import { TimeInterval } from 'types/search/chart'
import { calculateTimeInterval } from 'lib/search/calculate-time-interval'

const LOG_EVENTS_COUNT_QUERY = `
  query LogEventsCount($filter: LogEventsCountFilter) {
    logEventsCount(filter: $filter) {
      items {
        dateTime
        count
      }
    }
  }
`

interface LogEventsCountResponse {
  data: {
    logEventsCount: {
      items: Array<{
        dateTime: string
        count: number
      }>
    }
  }
}

interface LogEventsCountFilter {
  dateRange?: {
    from?: string | null
    to?: string | null
  }
  attributeValues?: Array<{
    attributeCode: string
    attributeValue: string
  }>
  interval: TimeInterval
}

/**
 * Fetch log events count from GraphQL API
 * Uses common query filters from selector (memoized)
 * Errors are caught by global error handler
 */
export const fetchLogEventsCount = async (): Promise<void> => {
  dispatch(SET_FETCHING_LOG_EVENTS_COUNT_DATA, true)

  try {
    // Get filters from selector (memoized, consistent across all queries)
    const commonFilters = getCommonQueryFilters()

    // Calculate the appropriate interval based on the date range
    const interval = calculateTimeInterval(
      commonFilters.dateRange.from,
      commonFilters.dateRange.to,
    )

    // Build filter with spread (includes all common filters + any future additions)
    const filter: LogEventsCountFilter = {
      ...commonFilters,
      interval,
    }

    const response = await apiClient.post<LogEventsCountResponse>('/graphql', {
      query: LOG_EVENTS_COUNT_QUERY,
      variables: {
        filter: Object.keys(filter).length > 0 ? filter : undefined,
      },
    })

    const items = response.data.data.logEventsCount.items

    // Map from GraphQL response to our internal format
    const chartData = items.map((item) => ({
      date: item.dateTime,
      count: item.count,
    }))

    // Save chart data with the interval used
    dispatch(SET_LOG_EVENTS_COUNT_DATA, { data: chartData, interval })

    log.debug('Chart data loaded', { dataPoints: items.length, interval })
  } finally {
    // In demo mode, keep loader active if no data - covers both
    // empty API response and error cases (retry will follow)
    if (!(isDemoLoading() && getChartData().length === 0)) {
      dispatch(SET_FETCHING_LOG_EVENTS_COUNT_DATA, false)
    }
  }
}
